import Vue from 'vue'
import { GetterTree, MutationTree, ActionTree } from 'vuex'
import { arrayUnion, arrayRemove } from 'vuex-easy-firestore'
import _find from 'lodash/find'
import _sampleSize from 'lodash/sampleSize'

import { dataService } from '@/service/firebase'

import { IAssociation } from '@/models'
import i18n from '@/i18n'

class State {
    _associations: Array<IAssociation> = []
    _randomAssociations: Array<IAssociation> = []
}

const getters = {
    associations( state ) {
        return state._associations
    },

    associationById( state ) {
        return ( id: string ) => _find( state._associations, { id: id } )
    },

    randomAssociations( state ) {
        return state._randomAssociations
    },

    totalAssociations( state ) {
        return Object.keys( state._associations ).length
    },

    votesByAssociationId( state ) {
        return ( id: string ) => {
            const associationById = _find( state._associations, { id: id } )

            return associationById ? associationById.relatedUsers : []
        }
    }
} as GetterTree<State, any>

const mutations = {
    setRandomAssociations( state ) {
        Vue.set( state, '_randomAssociations', _sampleSize( state._associations, 6 ) )
    }
} as MutationTree<State>

const actions = {
    createLocalAssociation( { dispatch, rootState }, associationData: { contentRaw: string; infoOnUpdate: boolean } ) {
        const localAssociations = localStorage.getItem( 'localAssociations' )
        const createdAssociation = {
            contentFormatted: '',
            contentRaw: associationData.contentRaw,
            dateCreated: dataService.Timestamp.fromDate( new Date ),
            infoOnUpdate: associationData.infoOnUpdate,
            isEditorialAssociation: false,
            published: false,
            relatedComments: [],
            relatedUser: rootState.usersModule._currentUser.id,
            relatedUsers: [],
            relatedImage: rootState.imagesModule._currentImage.id,
            language: i18n.locale
        }

        if ( !localAssociations ) {
            localStorage.setItem( 'localAssociations', JSON.stringify( [createdAssociation] ) )
        } else {
            const existingAssociations = JSON.parse( localAssociations )

            existingAssociations.push( createdAssociation )
            localStorage.setItem( 'localAssociations', JSON.stringify( existingAssociations ) )
        }
    },

    createAssociation( { dispatch, rootState }, associationData: { contentRaw: string; infoOnUpdate: boolean } ) {
        dispatch( 'set', {
            contentFormatted: '',
            contentRaw: associationData.contentRaw,
            dateCreated: dataService.Timestamp.fromDate( new Date ),
            infoOnUpdate: associationData.infoOnUpdate,
            isEditorialAssociation: false,
            published: false,
            relatedComments: [],
            relatedUser: rootState.usersModule._currentUser.id,
            relatedUsers: [],
            relatedImage: rootState.imagesModule._currentImage.id,
            language: i18n.locale
        } )
        .then( async ( result ) => {
            await dispatch( 'imagesModule/setRelatedAssociation', result, { root: true } )
            await dispatch( 'usersModule/setRelatedAssociation', result, { root: true } )
        } )
        .catch( error => console.error( error ) )
    },

    setRelatedComment( { state, getters, dispatch }, data: { commentId: string; associationId: string } ) {
        dispatch( 'patch', {
            id: data.associationId,
            relatedComments: arrayUnion( data.commentId )
        } )
    },

    voteForAssociation( { dispatch, rootState }, association: IAssociation ) {
        const usersVoted = association.relatedUsers
        const currentUser = rootState.usersModule._currentUser

        if ( usersVoted.indexOf( currentUser.id ) === -1 ) {
            dispatch( 'patch', {
                id: association.id,
                relatedUsers: arrayUnion( currentUser.id )
            } )
            .then( async () => {
                await dispatch( 'usersModule/setRelatedVote', association.id, { root: true } )
                await dispatch( 'imagesModule/setRelatedAssociation', association.id, { root: true } )
            } )
            .catch( error => console.error( error ) )
        }
    },

    removeVoteForAssociation( { dispatch, rootState }, association: IAssociation ) {
        const usersVoted = association.relatedUsers
        const currentUser = rootState.usersModule._currentUser
        const userIndex = usersVoted.indexOf( currentUser.id )

        if ( userIndex !== -1 ) {
            dispatch( 'patch', {
                id: association.id,
                relatedUsers: arrayRemove( currentUser.id )
            } )
            .then( async () => {
                await dispatch( 'usersModule/removeRelatedVote', association.id, { root: true } )
                await dispatch( 'imagesModule/removeRelatedAssociation', association.id, { root: true } )
            } )
            .catch( error => console.error( error ) )
        }
    }
} as ActionTree<State, any>

const AssociationModule = {
    firestorePath: 'Associations',
    firestoreRefType: 'collection',
    moduleName: 'associationsModule',
    statePropName: '_associations',
    namespaced: true,
    state: new State(),
    getters,
    mutations,
    actions
}

export default AssociationModule